import type { LiHTMLAttributes, ReactNode } from "react";
import clsx from "clsx";
import styles from "./Row.module.scss";

/**
 * How a row is FRAMED.
 *
 * `list` (default) is the card-list row: no frame of its own, separated from its neighbours by a hairline,
 * inset to line up with a `Card` header, full-bleed hover. It is the shape the shipped sheet declares for
 * `.contact-row, .friend-row, .team-row`.
 *
 * `framed` is the same row wearing its own frame — a bordered pill on paper, with no hairline and no card
 * inset — for a row that lives INSIDE a form section or a chip line rather than in a card list. It is not a
 * different component: same element, same anatomy, same interactions. Only the frame moves.
 */
export type RowVariant = "list" | "framed";

/** How tightly a `framed` row is packed. `comfortable` (default) is the editor row; `compact` is the chip
 *  line — smaller type, 3px of block padding, tighter gap. Ignored by `list`, which has one density. */
export type RowDensity = "comfortable" | "compact";

/**
 * The `<ul>` a set of `<Row>`s lives in.
 *
 * **`variant` must match the rows inside it**, because the container owns the thing a row cannot see: how
 * rows are separated. `list` rows carry their own hairlines and sit flush; `framed` rows are discrete
 * objects and need a gap between them.
 *
 * `wrap` turns the column into a wrapping line — the chip-row flow, where each row is content-width instead
 * of full-width. It is a property of the CONTAINER, not the row: the same framed row is a full-width row in
 * a stack and a chip in a line.
 *
 * `ordered` renders an `<ol>`. Reach for it when the ORDER is meaning rather than arrangement — a show's
 * bill runs opener → headliner, and a screen reader should be told that.
 */
export function Rows({
  className,
  children,
  variant = "list",
  wrap = false,
  ordered = false,
}: {
  className?: string;
  children: ReactNode;
  variant?: RowVariant;
  wrap?: boolean;
  ordered?: boolean;
}) {
  const List = ordered ? "ol" : "ul";
  return (
    <List className={clsx(styles.rows, className)} data-variant={variant} data-wrap={wrap || undefined}>
      {children}
    </List>
  );
}

export interface RowProps extends Omit<LiHTMLAttributes<HTMLLIElement>, "onClick"> {
  /** Usually an `<Avatar>`. */
  avatar?: ReactNode;
  /** The primary line. Badges/pills may be inlined here — the name is a flex line. */
  name: ReactNode;
  /** The secondary line — mono, per the spec's `__meta`. */
  sub?: ReactNode;
  /**
   * Controls at the row's end. Deliberately OUTSIDE the click target: a row that opens something and
   * also carries a Remove button cannot nest those two buttons.
   */
  trailing?: ReactNode;
  /** When given, the avatar + text become one button. Omit for a static row. */
  onOpen?: () => void;
  /** Disables the open affordance (a pending invite has nothing to open yet). */
  disabled?: boolean;
  /** Held-back styling for an invited-but-not-accepted row. */
  pending?: boolean;
  /** See {@link RowVariant}. Keep it the same as the `<Rows>` around it. */
  variant?: RowVariant;
  /** See {@link RowDensity}. `framed` only. */
  density?: RowDensity;
  className?: string;
}

/**
 * One list row: avatar · name + sub · trailing controls.
 *
 * The shipped sheet declares `.contact-row, .friend-row, .team-row` as a SINGLE rule — crew, teams and
 * friends are the same row — so this is one component rather than the four near-copies we had.
 *
 * **`framed` extends that argument to the editor and the chip line** (2026-08-26). Booking drew this same
 * anatomy four more times: the show editor's lineup chip and member row, and the show preview's lineup and
 * member chips. The reference draws them as ONE control — `.lineup-pill` and `.member-chip--editable` are
 * the same nine declarations, and the lineup pill reuses the member chip's own avatar class — and our own
 * preview had already collapsed its two onto one class. Only the editor had two, and they measured 7
 * declarations with 6 identical. So the frame became a variant here rather than a fifth near-copy there.
 *
 * **`name` is a flex line, and that is where inline qualifiers go** — a role, a "You" marker, an RSVP word.
 * Give the primary text its own span if it must truncate; this line cannot ellipsize its own children.
 */
export function Row({
  avatar, name, sub, trailing, onOpen, disabled, pending,
  variant = "list", density = "comfortable", className, ...rest
}: RowProps) {
  const main = (
    <>
      {avatar}
      <span className={styles.rowMain}>
        <span className={styles.rowName}>{name}</span>
        {sub != null && sub !== "" && <span className={styles.rowSub}>{sub}</span>}
      </span>
    </>
  );

  return (
    <li
      className={clsx(styles.row, className)}
      data-pending={pending || undefined}
      data-variant={variant}
      data-density={variant === "framed" ? density : undefined}
      {...rest}
    >
      {onOpen ? (
        <button type="button" className={styles.rowButton} onClick={onOpen} disabled={disabled}>
          {main}
        </button>
      ) : (
        <div className={styles.rowStatic}>{main}</div>
      )}
      {trailing}
    </li>
  );
}
